<template>
    <form-wrapper @form-submit="submit">
        <form-row class="create-company__choose-subdomain-form">
            <form-cell width="third" class="create-company__choose-subdomain-cell">
                <text-input
                    :formData="data"
                    :label="$t('pages.company.create_company.subdomain')"
                    :placeholder="$t('pages.company.create_company.enter_subdomain')"
                    :isRequired="true"
                    type="text"
                    valuePath="subdomain"
                    @change="change"
                ></text-input>
            </form-cell>
            <form-cell v-if="!isPhoneExtraSmall" width="third" class="create-company__choose-subdomain-cell">
                <div class="create-company__choose-subdomain-extension">
                    <input
                        class="create-company__choose-subdomain-extension__input"
                        :placeholder="extensionPlacholder"
                        disabled="disabled"
                        type="text"
                    />
                </div>
            </form-cell>
        </form-row>
        <div class="create-company__choose-subdomain-text">
            <span class="create-company__choose-subdomain-span">*</span>
            {{ $t('pages.company.create_company.subdomain_footnote') }}
        </div>
        <common-page-header
            class="create-company__choose-subdomain-header"
            :heading="$t('pages.company.create_company.choose_theme')"
            size="large"
        ></common-page-header>
        <common-content-spacer height="medium" class="create-company__choose-theme-spacer">
            <theme-card
                v-for="(type, index) in getEnums"
                :key="index"
                :type="type"
                :isActive="isActive(type)"
                @theme-preview="previewTheme"
                @click="chooseTheme(type)"
            ></theme-card>
        </common-content-spacer>
        <div class="create-company__choose-subdomain-button">
            <common-button type="primary" size="small" :isFluid="true" :label="$t('common.microcopy.save')"></common-button>
        </div>
        <common-pdf-preview v-if="isPreviewPdfOpen" :url="selectedDocument.src">
            <common-buttons align="justify" class="review-pdf__modal-buttons">
                <common-button
                    class="review-pdf__modal-buttons__button-close"
                    :label="$t('common.microcopy.back')"
                    @click="closeModal"
                ></common-button>
                <common-button
                    class="review-pdf__modal-buttons__button-choose"
                    :label="$t('pages.company.create_company.choose_theme')"
                    @click="selectTheme"
                ></common-button>
            </common-buttons>
        </common-pdf-preview>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import ThemeCard from 'components/page-elements/subdomain/ThemeCard'
    import ThemeEnum from 'enums/theme.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [FormMixin, WindowResizeMixin],

        components: {
            ThemeCard,
        },

        data() {
            return {
                activeTheme: ThemeEnum.WHITE.toLowerCase(),
                isPreviewPdfOpen: false,
                selectedDocument: null,
            }
        },

        computed: {
            getEnums() {
                return Object.entries(ThemeEnum).reduce((acc, [key, value]) => {
                    acc[key.toLowerCase()] = value.toLowerCase()
                    return acc
                }, {})
            },

            isPhoneExtraSmall() {
                return this.isSmartPhoneExtraSmall ? true : false
            },

            extensionPlacholder() {
                let url = process.env.VUE_APP_URL
                const regex = /(https?:\/\/)?([^/]+)\//
                const match = url.match(regex)
                return match ? `.${match[2]}` : ''
            },
        },

        methods: {
            previewTheme(theme) {
                this.selectedDocument = {
                    theme,
                    src: `${env('VUE_APP_PUBLIC_CONTENT_BUCKET')}documents/theme-${theme}.pdf`,
                }
                this.isPreviewPdfOpen = true
            },

            closeModal() {
                this.isPreviewPdfOpen = false
            },

            submit() {
                const data = {
                    subdomain: this.data.subdomain,
                    theme: this.activeTheme.toLowerCase(),
                }
                this.emitEvent({ name: 'subdomain-set', payload: data })
            },

            isActive(type) {
                return this.activeTheme === type
            },

            selectTheme() {
                this.activeTheme = this.selectedDocument.theme
                this.closeModal()
            },
            chooseTheme(type) {
                this.activeTheme = type
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .create-company {
        &__choose-subdomain {
            &-cell {
                padding-left: 0px;
                padding-right: 0px;

                @include breakpoint(smartphone-large) {
                    display: contents;
                    margin: auto;
                    text-align: center;
                }
            }

            &-cell > div > :last-child {
                border-top-right-radius: unset;
                border-bottom-right-radius: unset;
            }

            &-form {
                justify-content: center;
                transform: translateX(10%);

                @include breakpoint(tablet-small) {
                    transform: none;
                    display: flex;
                }
            }
            &-text {
                text-align: center;
                color: $subdomain-light-grey;
                margin: 5px 0 20px 0;
            }
            &-span {
                color: $subdomain-blue;
            }
            &-header {
                margin-top: 50px;
                text-align: center;
            }
            &-button {
                width: 200px;
                margin: 30px auto;
            }

            &-extension {
                margin-top: 26.5px;

                @include breakpoint(smartphone-large) {
                    margin-top: 26px;
                }

                &__input {
                    background-color: $color--bg-input;
                    border: 1px solid $color--bg-star-2;
                    height: $height--input;
                    padding: 0;
                    border-left: unset;
                    font-family: $font-family--base;
                    font-size: $font-size--form;
                    text-align: center;
                    width: 180px;

                    border-top-right-radius: 4px !important;
                    border-bottom-right-radius: 4px !important;
                }
            }
        }

        &__choose-theme-spacer {
            text-align: center;
            display: flex;
            justify-content: center;
            margin-top: 30px;

            @include breakpoint(tablet-small) {
                display: block;
            }
        }
    }

    .review-pdf__modal-buttons {
        margin: 30px 30px 10px 30px;

        &__button {
            &-close {
                border: 1px solid $color-bright-red;
                color: $color-white;
            }

            &-close:hover {
                opacity: 0.8;
            }

            &-choose {
                background-color: $color-bright-red;
                color: $color-white;
            }

            &-choose:hover {
                opacity: 0.8;
            }
        }
    }
</style>
